import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { UserPlus, FileText, FileSpreadsheet, ArrowRight, CheckCircle2, AlertCircle, Lock, Crown, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import PaywallModal from '../components/PaywallModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GSTDashboard = () => {
  const navigate = useNavigate();
  const { isLoggedIn, isAdmin, isGstr1Paid, isGstr3bPaid } = useAuth();
  const [showPaywall, setShowPaywall] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);

  // Unlock flags derived from the new /portal purchases (works for GST Complete, GSTR-1, GSTR-3B individual plans)
  const [portalUnlock, setPortalUnlock] = useState({ gstr1: false, gstr3b: false });

  useEffect(() => {
    const token = localStorage.getItem('practiq_portal_token');
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/user-auth/my-purchases`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        const plans = new Set(
          (data.purchases || [])
            .filter((p) => (p.status || '').toLowerCase() === 'active')
            .map((p) => (p.plan || '').toLowerCase())
        );
        const has = (...keys) => keys.some((k) => plans.has(k));
        const isGstComplete = has('gst-combo', 'gst_complete_monthly', 'gst_complete_yearly', 'gst_complete', 'gst-complete');
        const isG1 = has('gstr1', 'gstr1_monthly', 'gstr1_yearly') || isGstComplete;
        const isG3B = has('gstr3b', 'gstr3b_monthly', 'gstr3b_yearly') || isGstComplete;
        if (!cancelled) setPortalUnlock({ gstr1: isG1, gstr3b: isG3B });
      } catch (_) { /* fail silently */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const modules = [
    {
      id: 'registration',
      title: 'GST Registration',
      subtitle: 'New Registration Application',
      description: 'Apply for new GST registration. Fill Part A with basic details and Part B with business information.',
      icon: UserPlus,
      gradient: 'from-emerald-400 to-teal-500',
      path: '/gst/registration',
      isFree: true,
      features: ['PAN Verification', 'OTP Authentication', 'TRN Generation', 'Multi-step Form']
    },
    {
      id: 'gstr1',
      title: 'GSTR-1',
      subtitle: 'Details of Outward Supplies',
      description: 'File your monthly/quarterly return for outward supplies. Includes B2B, B2C, exports, and amendments.',
      icon: FileText,
      gradient: 'from-blue-400 to-cyan-500',
      path: '/simulator/gstr1',
      isFree: false,
      features: ['12+ Invoice Tables', 'HSN Summary', 'Auto Tax Calculation', 'Document Summary']
    },
    {
      id: 'gstr3b',
      title: 'GSTR-3B',
      subtitle: 'Monthly Summary Return',
      description: 'File your monthly self-declaration return. Summary of outward supplies, ITC claimed, and tax payment.',
      icon: FileSpreadsheet,
      gradient: 'from-purple-400 to-pink-500',
      path: '/simulator/gstr3b',
      isFree: false,
      features: ['6 Section Summary', 'ITC Computation', 'Interest Calculator', 'Payment Challan']
    }
  ];

  // Check if module is unlocked
  const isModuleUnlocked = (moduleId) => {
    // GST Registration is always free
    if (moduleId === 'registration') return true;
    
    // Admin has full access
    if (isAdmin) return true;
    
    // Check legacy AuthContext flags OR new portal-purchase flags
    if (moduleId === 'gstr1')  return isGstr1Paid  || portalUnlock.gstr1;
    if (moduleId === 'gstr3b') return isGstr3bPaid || portalUnlock.gstr3b;
    
    return false;
  };

  const handleModuleClick = (module) => {
    // GST Registration is free but requires portal login.
    // The /gst/registration route is wrapped in <RequirePortalLogin> so the gate
    // page itself handles the redirect-to-login flow with a clean UX.
    if (module.id === 'registration') {
      navigate(module.path);
      return;
    }

    // Other modules (GSTR-1, GSTR-3B…) — check payment unlock status
    if (isModuleUnlocked(module.id)) {
      navigate(module.path);
    } else {
      // Show paywall
      setSelectedModule(module.id);
      setShowPaywall(true);
    }
  };

  return (
    <Layout>
      {/* Page Header */}
      <div className="w-full bg-[#0D1B2A] text-white py-8">
        <div className="w-full px-6 lg:px-16 xl:px-24">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-2">
            <span className="cursor-pointer hover:text-teal-400 transition-colors" onClick={() => navigate('/')}>Home</span>
            <span>/</span>
            <span className="text-teal-400">GST Services</span>
          </div>
          <h1 className="text-3xl font-bold">GST Services Dashboard</h1>
          <p className="text-gray-400 mt-2">Select a module to practice GST compliance procedures</p>
        </div>
      </div>

      {/* Info Banner */}
      <div className="w-full bg-[#1B2838] border-b border-white/10">
        <div className="w-full px-6 lg:px-16 xl:px-24 py-3">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3 text-sm text-gray-300">
              <AlertCircle className="w-5 h-5 text-teal-400" />
              <span>
                Practice GST filing in a <strong className="text-teal-400">risk-free simulation environment</strong>
              </span>
            </div>
            {isAdmin && (
              <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                <Crown className="w-3 h-3 mr-1" />
                Admin Access • All Modules Unlocked
              </Badge>
            )}
            {!isAdmin && (isGstr1Paid || isGstr3bPaid) && (
              <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                {isGstr1Paid && isGstr3bPaid ? 'Full GST Bundle' : isGstr1Paid ? 'GSTR-1 Unlocked' : 'GSTR-3B Unlocked'}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Module Cards */}
      <div className="w-full bg-[#0A0F1C] py-12">
        <div className="w-full px-6 lg:px-16 xl:px-24">
          <div className="grid md:grid-cols-3 gap-6">
            {modules.map((module) => {
              const isUnlocked = isModuleUnlocked(module.id);
              const ModuleIcon = module.icon;
              
              return (
                <Card 
                  key={module.id}
                  className="bg-[#111827] border border-white/10 hover:border-teal-500/50 hover:shadow-xl hover:shadow-teal-500/5 transition-all duration-300 cursor-pointer group overflow-hidden"
                  onClick={() => handleModuleClick(module)}
                  data-testid={`${module.id}-module-card`}
                >
                  <div className={`h-1.5 bg-gradient-to-r ${module.gradient}`}></div>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className={`w-14 h-14 bg-gradient-to-r ${module.gradient} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg`}>
                        <ModuleIcon className="w-7 h-7 text-white" />
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        {module.isFree ? (
                          <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/20">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            Free
                          </Badge>
                        ) : isUnlocked ? (
                          <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/20">
                            <Sparkles className="w-3 h-3 mr-1" />
                            Unlocked
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 hover:bg-amber-500/20">
                            <Lock className="w-3 h-3 mr-1" />
                            ₹99/month
                          </Badge>
                        )}
                      </div>
                    </div>
                    <CardTitle className="text-xl text-white mt-4">
                      {module.title}
                    </CardTitle>
                    <p className="text-sm text-teal-400 font-medium">{module.subtitle}</p>
                    <CardDescription className="text-gray-400 mt-2 text-sm">
                      {module.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <div className="space-y-2 mb-5">
                      {module.features.map((feature, index) => (
                        <div key={index} className="flex items-center gap-2 text-sm text-gray-300">
                          <div className="w-1.5 h-1.5 bg-teal-400 rounded-full" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                    
                    {/* Dynamic Button based on unlock status */}
                    {module.isFree ? (
                      <Button 
                        className={`w-full h-11 border-0 bg-gradient-to-r ${module.gradient} hover:opacity-90 text-white`}
                      >
                        Open Module
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    ) : isUnlocked ? (
                      <Button 
                        className={`w-full h-11 border-0 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white`}
                      >
                        <Sparkles className="w-4 h-4 mr-2" />
                        Enter Simulator
                      </Button>
                    ) : (
                      <Button 
                        className="w-full h-11 border-0 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white"
                      >
                        <Lock className="w-4 h-4 mr-2" />
                        Unlock Now
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Quick Stats */}
          <div className="mt-12 grid md:grid-cols-4 gap-4">
            {[
              { label: 'Registration', status: 'Free', color: 'emerald' },
              { label: 'GSTR-1', status: isModuleUnlocked('gstr1') ? 'Unlocked' : 'Locked', color: isModuleUnlocked('gstr1') ? 'emerald' : 'amber' },
              { label: 'GSTR-3B', status: isModuleUnlocked('gstr3b') ? 'Unlocked' : 'Locked', color: isModuleUnlocked('gstr3b') ? 'emerald' : 'amber' },
              { label: 'ITR', status: 'Coming Soon', color: 'gray' }
            ].map((item, i) => (
              <div key={i} className={`p-4 rounded-xl bg-${item.color}-500/10 border border-${item.color}-500/20`}>
                <p className="text-sm text-gray-400">{item.label}</p>
                <p className={`text-lg font-semibold text-${item.color}-400`}>{item.status}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Paywall Modal */}
      <PaywallModal 
        isOpen={showPaywall} 
        onClose={() => setShowPaywall(false)}
        feature={selectedModule}
        onSubscribe={() => {
          setShowPaywall(false);
        }}
      />
    </Layout>
  );
};

export default GSTDashboard;
